import { DateTime } from "luxon";

const AMEX_HEADER_ROW_INDEX = 6;
const AMEX_HEADER_START_COLUMN_INDEX = 0;
const ADDRESS_DETAIL_FIELD_KEY = "Address";
const EXT_DETS_FIELD_KEY = "Extended Details";

const AMEX_HEADERS = [
  "Date",
  "Description",
  "Amount",
  EXT_DETS_FIELD_KEY,
  "Appears On Your Statement As",
  ADDRESS_DETAIL_FIELD_KEY,
  "City/State",
  "Zip Code",
  "Country",
  "Reference",
  "Category",
];

const AMEX_DEFAULT_DETAIL_FIELDS = [
  ADDRESS_DETAIL_FIELD_KEY,
  EXT_DETS_FIELD_KEY,
];

const AMEX_DETAIL_FIELDS = [
  EXT_DETS_FIELD_KEY,
  "Appears On Your Statement As",
  ADDRESS_DETAIL_FIELD_KEY,
  "City/State",
  "Zip Code",
  "Country",
  "Reference",
  "Category",
];

const AMEX_DETAIL_FIELD_ALIASES = {
  zip: "Zip Code",
  zipcode: "Zip Code",
  "zip code": "Zip Code",
  "city state": "City/State",
  "city/state": "City/State",
  address: ADDRESS_DETAIL_FIELD_KEY,
  country: "Country",
  reference: "Reference",
  category: "Category",
  "extended details": EXT_DETS_FIELD_KEY,
  "appears on your statement as": "Appears On Your Statement As",
};

function normalizeHeaderName(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeInstitutionName(name) {
  return String(name || "")
    .trim()
    .toLowerCase();
}

function normalizeDetailFieldName(fieldName) {
  const normalized = normalizeHeaderName(fieldName);

  if (AMEX_DETAIL_FIELD_ALIASES[normalized]) {
    return AMEX_DETAIL_FIELD_ALIASES[normalized];
  }

  const matchedDetailField = AMEX_DETAIL_FIELDS.find(
    (field) => normalizeHeaderName(field) === normalized
  );

  return matchedDetailField || null;
}

function isAmexDataRowEmpty(row, headerIndexes) {
  return headerIndexes.every((idx) => {
    const cellValue = row?.[idx];
    return String(cellValue ?? "").trim() === "";
  });
}

function buildHeaderIndexes(headerRow) {
  const headerIndexes = {};
  const normalizedHeaderRow = headerRow.map((cell) =>
    normalizeHeaderName(cell)
  );

  AMEX_HEADERS.forEach((header, offset) => {
    const expectedIndex = AMEX_HEADER_START_COLUMN_INDEX + offset;
    const expectedHeader = normalizeHeaderName(header);
    const foundHeader = normalizedHeaderRow[expectedIndex];

    if (foundHeader !== expectedHeader) {
      throw new Error(
        `Unexpected Amex file format at row 7, column ${
          expectedIndex + 1
        }. Expected "${header}", found "${headerRow?.[expectedIndex] ?? ""}".`
      );
    }

    headerIndexes[header] = expectedIndex;
  });

  return headerIndexes;
}

function safeCellValue(row, index) {
  return String(row?.[index] ?? "").trim();
}

function toIsoDateFromAmex(rawDateValue, rowNumber) {
  const rawDate = String(rawDateValue ?? "").trim();
  let parsed = DateTime.fromFormat(rawDate, "MM/dd/yyyy");

  if (parsed.isValid) {
    return parsed.toISODate();
  }

  const excelSerial = Number(rawDate);
  if (!isNaN(excelSerial) && excelSerial > 59) {
    const base = DateTime.fromISO("1899-12-30");
    parsed = base.plus({ days: excelSerial });
    if (parsed.isValid) {
      return parsed.toISODate();
    }
  }

  if (!parsed.isValid) {
    throw new Error(
      `Invalid Amex date at row ${rowNumber}. Expected MM/DD/YYYY or Excel serial, found "${rawDate}".`
    );
  }

  return parsed.toISODate();
}

function pickRequestedDetails(row, headerIndexes, detailsToInclude) {
  const requestedFields =
    Array.isArray(detailsToInclude) && detailsToInclude.length > 0
      ? detailsToInclude
      : AMEX_DEFAULT_DETAIL_FIELDS;

  return requestedFields.reduce((acc, fieldName) => {
    const matchingHeader = AMEX_HEADERS.find(
      (header) => normalizeHeaderName(header) === normalizeHeaderName(fieldName)
    );

    if (!matchingHeader) {
      return acc;
    }

    const value = safeCellValue(row, headerIndexes[matchingHeader]);
    if (!value) {
      return acc;
    }

    return {
      ...acc,
      [matchingHeader]: value,
    };
  }, {});
}

export const amexAdapter = {
  name: "amex",
  institutionAliases: ["amex", "american express", "axp"],
  supportedDetailFields: [...AMEX_DETAIL_FIELDS],
  canHandleInstitutionName(institutionName) {
    const normalized = normalizeInstitutionName(institutionName);
    return this.institutionAliases.includes(normalized);
  },
  validateDetailsToInclude(detailsToInclude = []) {
    const requestedFields =
      Array.isArray(detailsToInclude) && detailsToInclude.length > 0
        ? detailsToInclude
        : AMEX_DEFAULT_DETAIL_FIELDS;

    const normalized = [];
    const invalid = [];
    const seenNormalized = new Set();

    requestedFields.forEach((fieldName) => {
      const normalizedFieldName = normalizeDetailFieldName(fieldName);

      if (!normalizedFieldName) {
        invalid.push(String(fieldName));
        return;
      }

      if (!seenNormalized.has(normalizedFieldName)) {
        seenNormalized.add(normalizedFieldName);
        normalized.push(normalizedFieldName);
      }
    });

    return {
      normalized,
      invalid,
      allowed: [...AMEX_DETAIL_FIELDS],
    };
  },
  extractTransactions(rows, options = {}) {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    const headerRow = rows[AMEX_HEADER_ROW_INDEX] || [];
    const headerIndexes = buildHeaderIndexes(headerRow);
    const transactions = [];

    for (
      let rowIndex = AMEX_HEADER_ROW_INDEX + 1;
      rowIndex < rows.length;
      rowIndex += 1
    ) {
      const row = rows[rowIndex] || [];

      if (isAmexDataRowEmpty(row, Object.values(headerIndexes))) {
        break;
      }

      const details = pickRequestedDetails(
        row,
        headerIndexes,
        options.detailsToInclude
      );

      const rowNumber = rowIndex + 1;
      const isoDate = toIsoDateFromAmex(row[headerIndexes.Date], rowNumber);
      const rawAmount = safeCellValue(row, headerIndexes.Amount);
      const invertedAmount = String(-1 * parseFloat(rawAmount));

      transactions.push({
        rowNumber,
        date: isoDate,
        amount: invertedAmount,
        description: row[headerIndexes.Description],
        details,
      });
    }

    return transactions;
  },
};
